import React from 'react';
import { formatLeave } from '../lib/leaveUnits';

/**
 * "Other leave taken (year)" — the sidebar breakdown of NON-DEDUCTING leave a
 * person has actually taken: Sick, Unpaid, Work From Home, Parental… the kinds
 * that never came off their annual allowance and so are invisible on the balance
 * card above. A competitor calls this "Non-deductible leave"; the point is the
 * same — "how many sick days / unpaid days did I take this year?".
 *
 * Data comes from GET /leave/taken-by-type, which returns EVERY type with any
 * approved booking (holiday included) so the same figures can feed a payroll
 * export later. Here we deliberately show only the non-deducting subset with a
 * non-zero total: Annual Leave is already the whole card above, and a type with
 * nothing taken is noise. Renders nothing at all when there's nothing to show,
 * so the section simply doesn't appear for a person who's only taken holiday.
 *
 * Unit-aware via the shared formatLeave: a person on hours sees "12h", a person
 * on days sees "1.5 days", straight off the same row (taken_days + taken_minutes).
 */
export default function OtherLeaveTaken({ items, unit = 'days', year }) {
  const rows = (items || [])
    .filter((t) => !t.deducted && ((t.taken_minutes || 0) > 0 || (t.taken_days || 0) > 0));

  if (!rows.length) return null;

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <p className="text-sm text-muted-foreground font-medium">
        Other leave taken{year ? ` (${year})` : ''}
      </p>

      {rows.map((t) => (
        <div key={t.leave_type_id} className="flex justify-between text-sm items-center">
          <span className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: t.color || '#f59e0b' }}
            />
            {t.leave_type_name}
          </span>
          <span className="font-medium">
            {/* Time in lieu is always hours and minutes (DESIGN-toil.md C). */}
            {formatLeave(t.taken_days, { unit: t.is_toil ? 'hours' : unit, minutes: t.taken_minutes })}
          </span>
        </div>
      ))}
    </div>
  );
}
